/**
 * Quem está falando — lapela do médico × microfones da sala (spec 016,
 * fase 2). Sem navegador aqui: entra nível, sai "médico" / "sala".
 *
 * Por que funciona: a lapela fica a um palmo da boca do médico. Quando ELE
 * fala, ela sobe muito acima da mesa; quando o paciente fala, a lapela
 * também ouve, mas a diferença pra mesa cai. A regra é essa diferença
 * (energia média dos últimos ~150 ms): lapela ≥ `limiarDb` acima do mais
 * alto da sala = médico; senão = sala (paciente ou acompanhante — os dois
 * caem no mesmo microfone, quem separa é o modelo, pelo conteúdo).
 *
 * Só vale com um microfone marcado "Médico" E pelo menos mais um em uso
 * (o Hmaston só na lapela + a mesa CAP 200, 16/10/2026). O receptor que
 * mistura dois transmissores não serve pra lapela.
 */

import { paraDb } from './dsp';

export type Falante = 'medico' | 'sala';

/** Diferença padrão lapela − sala pra contar como médico. Ajustável em Configurações. */
export const LIMIAR_FALANTE_PADRAO_DB = 6;

/** Média da energia (s). */
const SUAVE_S = 0.15;
/** Quem vem novo precisa durar isto pra trocar (sílaba solta não vira troca). */
const TROCA_S = 0.15;

/** Diferença em dB entre duas energias (potências), sem dividir por zero. */
function diferencaDb(pMed: number, pSala: number): number {
  if (pSala <= 0) return pMed > 0 ? Infinity : 0;
  if (pMed <= 0) return -Infinity;
  return 10 * Math.log10(pMed / pSala);
}

export class DetectorFalante {
  private pMed = 0;
  private pSala = 0;
  private atual: Falante | null = null;
  private cand: Falante | null = null;
  private candS = 0;

  constructor(private limiarDb: number = LIMIAR_FALANTE_PADRAO_DB) {}

  setLimiar(db: number): void {
    this.limiarDb = db;
  }

  /** Diferença lapela − sala agora (dB, média curta). Pra tela de ajuste. */
  get diferenca(): number {
    return diferencaDb(this.pMed, this.pSala);
  }

  /**
   * Um bloco de som. `med` = nível (RMS) da lapela; `sala` = o mais alto dos
   * outros; `voz` = algum deles passou do corte de ruído; `dt` = duração do
   * bloco em segundos.
   */
  push(med: number, sala: number, voz: boolean, dt: number): Falante | null {
    const a = Math.exp(-dt / SUAVE_S);
    this.pMed = a * this.pMed + (1 - a) * med * med;
    this.pSala = a * this.pSala + (1 - a) * sala * sala;
    const c: Falante | null = !voz ? null : this.diferenca >= this.limiarDb ? 'medico' : 'sala';
    if (c === this.atual) {
      this.cand = c;
      this.candS = 0;
      return this.atual;
    }
    if (c !== this.cand) {
      this.cand = c;
      this.candS = 0;
    }
    this.candS += dt;
    if (this.candS >= TROCA_S) {
      this.atual = c;
      this.candS = 0;
    }
    return this.atual;
  }
}

/** Nível em dB pra mostrar (−∞ vira −99). */
export function dbLegivel(nivel: number): number {
  const db = paraDb(nivel);
  return Number.isFinite(db) ? db : -99;
}

/** Quanto (ms) cada um falou numa janela do Ao vivo. */
export interface JanelaFala {
  medico: number;
  sala: number;
}

/**
 * O pedido saiu de fala só da sala? A sala falou de verdade (≥ 600 ms) e a
 * lapela quase nada (< 300 ms). Janela vazia (nada medido) = não.
 */
export function falaSoDaSala(j: JanelaFala): boolean {
  return j.sala >= 600 && j.medico < 300;
}

export interface TrechoFala {
  /** Segundos desde o começo da gravação. */
  de: number;
  ate: number;
  quem: Falante;
}

/** Trecho mais curto que isto some (tosse, "hum"). */
const TRECHO_MIN_S = 0.4;
/** Silêncio menor que isto entre dois trechos do mesmo falante junta os dois. */
const EMENDA_S = 1;

/**
 * Tira os trechos curtos e junta os do mesmo falante separados por pausa
 * curta. A marcação do Ao vivo usa corte e emenda menores: guarda o "Não."
 * de resposta e as pausas entre uma fala e outra.
 */
export function compactar(trechos: readonly TrechoFala[], minS = TRECHO_MIN_S, emendaS = EMENDA_S): TrechoFala[] {
  const out: TrechoFala[] = [];
  for (const t of trechos) {
    if (t.ate - t.de < minS) continue;
    const ult = out[out.length - 1];
    if (ult && ult.quem === t.quem && t.de - ult.ate <= emendaS) ult.ate = t.ate;
    else out.push({ ...t });
  }
  return out;
}

/**
 * Linha do tempo de quem falou numa gravação. Recebe uma marca a cada
 * ~100 ms (tempo de GRAVAÇÃO, sem as pausas) e devolve os trechos.
 */
export class LinhaDoTempo {
  private feitos: TrechoFala[] = [];
  private aberto: TrechoFala | null = null;
  private fim = 0;

  marcar(t: number, quem: Falante | null): void {
    this.fim = Math.max(this.fim, t);
    if (this.aberto && this.aberto.quem === quem) {
      this.aberto.ate = t;
      return;
    }
    if (this.aberto) {
      this.aberto.ate = t;
      this.feitos.push(this.aberto);
    }
    this.aberto = quem ? { de: t, ate: t, quem } : null;
  }

  /** Fecha no fim da gravação. */
  fechar(t: number): void {
    this.marcar(t, null);
  }

  get duracao(): number {
    return this.fim;
  }

  trechos(minS = TRECHO_MIN_S, emendaS = EMENDA_S): TrechoFala[] {
    return compactar(this.aberto ? [...this.feitos, this.aberto] : this.feitos, minS, emendaS);
  }
}

const mmss = (s: number) => {
  const t = Math.max(0, Math.round(s));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

const ROTULO: Record<Falante, string> = { medico: 'Médico', sala: 'Paciente/acompanhante' };

/**
 * Texto pra IA junto do áudio. O Gemini junta os canais antes de ouvir —
 * separar quem fala tem que ir como texto. `medidoAte` = depois disso a
 * gravação não tem medida (áudio de antes da mistura, anexo…).
 */
export function textoLinhaDoTempo(trechos: readonly TrechoFala[], medidoAte?: number): string {
  const linhas = trechos.map((t) => `${mmss(t.de)}–${mmss(t.ate)} ${ROTULO[t.quem]}`);
  return [
    'LINHA DO TEMPO DE QUEM FALOU NA GRAVAÇÃO (medida pelos microfones, não por IA):',
    '"Médico" = microfone de lapela do médico; "Paciente/acompanhante" = microfone da sala (os microfones não separam paciente de acompanhante: decida pelo conteúdo).',
    'Use para separar o que o paciente ou o acompanhante relatou (queixa, história, antecedentes, medicações em uso) do que o médico disse (achados do exame, hipóteses, conduta). Não escreva rótulos de falante nem horários no prontuário.',
    ...linhas,
    ...(medidoAte !== undefined ? [`(depois de ${mmss(medidoAte)} a gravação não tem medida de quem falou)`] : []),
  ].join('\n');
}
